// frontend/src/components/PWAInstallBanner.js
//
// PWA install flow:
//   Visit 1       → InstallBanner shown (auto-install on Chrome/Android; manual guide on iOS)
//   Dismiss once  → wait 3 days, then show again
//   Dismiss twice → show ManualGuide once, then never again (until localStorage cleared)
//   Install       → never show again
//
// Module-level capture of beforeinstallprompt so we never miss it even if it
// fires before the component fully mounts.
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

let _deferredPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredPrompt = e;
  });
}

// ─── Storage helpers ──────────────────────────────────────────
const KEY = 'sn_pwa_install';
const THREE_DAYS = 3 * 24 * 60 * 60 * 1000;
// Wait for splash to fully clear (SPLASH_MIN_MS 2600 + 700ms fade + 300ms buffer)
const SHOW_DELAY_MS = 3600;

function load() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}'); }
  catch { return {}; }
}
function save(patch) {
  localStorage.setItem(KEY, JSON.stringify({ ...load(), ...patch }));
}

// ─── Device detection ─────────────────────────────────────────
function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// ─── Shared app icon chip ─────────────────────────────────────
// Two-div structure mirrors SplashScreen: outer holds border/shadow,
// inner holds the image + gradient mask that hides the "Staynivo" text.
// Ratio 1.271 (same as splash 178/140) leaves ~18 % padding each side;
// gradient mask fades at 77-84 % so text (which falls below that) is hidden.
function AppIcon({ size = 64, radius = 14 }) {
  const bs = Math.round(size * 1.271);
  return (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0,
      overflow: 'hidden', position: 'relative',
      border: '1.5px solid rgba(28,201,232,0.38)',
      boxShadow: '0 4px 16px rgba(0,140,200,0.28)',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundColor: '#090d1b',
        backgroundImage: 'url(/icons/icon-384x384.png)',
        backgroundSize: `${bs}px ${bs}px`,
        backgroundPosition: 'center top',
        backgroundRepeat: 'no-repeat',
        WebkitMaskImage: 'linear-gradient(to bottom, black 77%, transparent 84%)',
        maskImage: 'linear-gradient(to bottom, black 77%, transparent 84%)',
      }} />
    </div>
  );
}

// ─── Install banner (bottom sheet) ───────────────────────────
function InstallBanner({ visible, ios, onInstall, onDismiss, t }) {
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9500,
      padding: '0 12px calc(env(safe-area-inset-bottom, 0px) + 12px)',
      transform: visible ? 'translateY(0)' : 'translateY(110%)',
      transition: 'transform 0.4s cubic-bezier(0.25,0,0.15,1)',
      pointerEvents: visible ? 'auto' : 'none',
    }}>
      <div style={{
        maxWidth: 500, margin: '0 auto',
        background: 'linear-gradient(145deg,#0d1929 0%,#121f38 100%)',
        border: '1px solid rgba(28,201,232,0.22)',
        borderRadius: 20,
        boxShadow: '0 -4px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(28,201,232,0.07)',
        padding: '18px 18px 18px',
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
          <AppIcon size={60} radius={13} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'DM Sans','Inter',sans-serif", fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>
              <span style={{ color: '#ffffff' }}>Stay</span>
              <span style={{ color: '#1cc9e8' }}>nivo</span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(160,200,240,0.7)', marginTop: 2, fontFamily: "'DM Sans','Inter',sans-serif" }}>
              {t('pwa.prompt.tagline', 'Hotel Management System')}
            </div>
          </div>
          <button
            onClick={onDismiss}
            aria-label={t('common.close', 'Close')}
            style={{ background: 'none', border: 'none', color: 'rgba(160,200,240,0.4)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '4px 8px', flexShrink: 0 }}
          >×</button>
        </div>

        {/* Feature pills */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[
            ['⚡', t('pwa.feature.fast', 'Faster access')],
            ['📶', t('pwa.feature.offline', 'Works offline')],
            ['🔔', t('pwa.feature.push', 'Push alerts')],
          ].map(([icon, lbl]) => (
            <span key={lbl} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'rgba(28,201,232,0.08)',
              border: '1px solid rgba(28,201,232,0.18)',
              borderRadius: 20, padding: '3px 10px',
              fontSize: 11, color: 'rgba(180,225,255,0.85)',
              fontFamily: "'DM Sans','Inter',sans-serif",
            }}>{icon} {lbl}</span>
          ))}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 9 }}>
          <button
            onClick={onDismiss}
            style={{
              flex: 1, padding: '11px 0', borderRadius: 11,
              border: '1px solid rgba(28,201,232,0.18)',
              background: 'transparent',
              color: 'rgba(160,200,240,0.7)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              fontFamily: "'DM Sans','Inter',sans-serif",
            }}
          >{t('pwa.prompt.notNow', 'Not now')}</button>
          <button
            onClick={onInstall}
            style={{
              flex: 2, padding: '11px 0', borderRadius: 11, border: 'none',
              background: 'linear-gradient(135deg,#0ea5c8,#1cc9e8)',
              color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'DM Sans','Inter',sans-serif",
              boxShadow: '0 4px 16px rgba(28,201,232,0.32)',
            }}
          >{ios ? t('pwa.prompt.showHow', 'Show me how') : t('pwa.prompt.install', 'Install App')}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Manual install guide (bottom sheet with tabs) ────────────
function ManualGuide({ visible, tab, onTabChange, onClose, t }) {
  const steps = {
    ios: [
      {
        icon: '🧭',
        title: t('pwa.ios.s1.title', 'Open in Safari'),
        desc: t('pwa.ios.s1.desc', 'This feature requires Safari. If you\'re in another browser, copy the URL and open it in Safari.'),
      },
      {
        icon: '⬆️',
        title: t('pwa.ios.s2.title', 'Tap the Share button'),
        desc: t('pwa.ios.s2.desc', 'Tap the Share icon (a box with an upward arrow) at the bottom of Safari.'),
      },
      {
        icon: '➕',
        title: t('pwa.ios.s3.title', 'Add to Home Screen'),
        desc: t('pwa.ios.s3.desc', 'Scroll down the share sheet and tap "Add to Home Screen".'),
      },
      {
        icon: '✅',
        title: t('pwa.ios.s4.title', 'Tap Add'),
        desc: t('pwa.ios.s4.desc', 'Tap "Add" in the top-right corner. StayNivo will appear on your home screen.'),
      },
    ],
    android: [
      {
        icon: '🌐',
        title: t('pwa.android.s1.title', 'Open in Chrome'),
        desc: t('pwa.android.s1.desc', 'This feature requires Chrome. If you\'re in another browser, copy the URL and open it in Chrome.'),
      },
      {
        icon: '⋮',
        title: t('pwa.android.s2.title', 'Open the menu'),
        desc: t('pwa.android.s2.desc', 'Tap the three-dot menu (⋮) in the top-right corner of Chrome.'),
      },
      {
        icon: '📲',
        title: t('pwa.android.s3.title', 'Add to Home Screen'),
        desc: t('pwa.android.s3.desc', 'Tap "Add to Home Screen" or "Install App" from the menu.'),
      },
      {
        icon: '✅',
        title: t('pwa.android.s4.title', 'Confirm'),
        desc: t('pwa.android.s4.desc', 'Tap "Add" or "Install" to confirm. StayNivo will appear on your home screen.'),
      },
    ],
  };

  const currentSteps = steps[tab] || steps.android;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 9499,
          background: 'rgba(0,5,15,0.68)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.35s',
          pointerEvents: visible ? 'auto' : 'none',
        }}
      />

      {/* Sheet */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9500,
        padding: '0 12px calc(env(safe-area-inset-bottom, 0px) + 12px)',
        transform: visible ? 'translateY(0)' : 'translateY(110%)',
        transition: 'transform 0.42s cubic-bezier(0.25,0,0.15,1)',
        pointerEvents: visible ? 'auto' : 'none',
      }}>
        <div style={{
          maxWidth: 500, margin: '0 auto',
          background: 'linear-gradient(145deg,#0d1929 0%,#121f38 100%)',
          border: '1px solid rgba(28,201,232,0.22)',
          borderRadius: 20,
          boxShadow: '0 -4px 40px rgba(0,0,0,0.55)',
          padding: 22,
          display: 'flex', flexDirection: 'column', gap: 16,
          maxHeight: '85vh', overflowY: 'auto',
        }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <AppIcon size={48} radius={11} />
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'DM Sans','Inter',sans-serif", fontSize: 16, fontWeight: 700, color: '#fff' }}>
                {t('pwa.manual.title', 'Install StayNivo')}
              </div>
              <div style={{ fontSize: 12, color: 'rgba(160,200,240,0.6)', marginTop: 2 }}>
                {t('pwa.manual.subtitle', 'Add to your home screen in 4 steps')}
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label={t('common.close', 'Close')}
              style={{ background: 'none', border: 'none', color: 'rgba(160,200,240,0.4)', fontSize: 22, cursor: 'pointer', padding: '4px 8px' }}
            >×</button>
          </div>

          {/* OS tabs */}
          <div style={{ display: 'flex', gap: 8 }}>
            {[['android', '🤖 Android'], ['ios', '🍎 iPhone / iPad']].map(([id, lbl]) => (
              <button
                key={id}
                onClick={() => onTabChange(id)}
                style={{
                  flex: 1, padding: '8px 4px', borderRadius: 10, cursor: 'pointer',
                  fontSize: 12, fontWeight: tab === id ? 700 : 400,
                  border: tab === id ? '1px solid rgba(28,201,232,0.45)' : '1px solid rgba(255,255,255,0.08)',
                  background: tab === id ? 'rgba(28,201,232,0.12)' : 'rgba(255,255,255,0.04)',
                  color: tab === id ? '#1cc9e8' : 'rgba(160,200,240,0.5)',
                  fontFamily: "'DM Sans','Inter',sans-serif",
                  transition: 'all 0.2s',
                }}
              >{lbl}</button>
            ))}
          </div>

          {/* Steps */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {currentSteps.map((step, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{
                  width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                  background: 'rgba(28,201,232,0.10)',
                  border: '1px solid rgba(28,201,232,0.28)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16,
                }}>{step.icon}</div>
                <div style={{ paddingTop: 4 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e0f0ff', lineHeight: 1.3, fontFamily: "'DM Sans','Inter',sans-serif" }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(155,195,240,0.7)', marginTop: 3, lineHeight: 1.5, fontFamily: "'DM Sans','Inter',sans-serif" }}>
                    {step.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Got it */}
          <button
            onClick={onClose}
            style={{
              padding: '12px 0', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg,#0ea5c8,#1cc9e8)',
              color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'DM Sans','Inter',sans-serif",
              boxShadow: '0 4px 16px rgba(28,201,232,0.28)',
            }}
          >{t('pwa.manual.gotIt', 'Got it, thanks!')}</button>
        </div>
      </div>
    </>
  );
}

// ─── Main orchestrator ────────────────────────────────────────
export default function PWAInstallBanner() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState(null);   // 'banner' | 'manual' | null
  const [visible, setVisible] = useState(false);
  const [manualTab, setManualTab] = useState(isIOS() ? 'ios' : 'android');

  // Listen for successful install (also fired by native prompt acceptance)
  useEffect(() => {
    const handler = () => {
      save({ installed: true });
      setVisible(false);
      setTimeout(() => setPhase(null), 400);
    };
    window.addEventListener('appinstalled', handler);
    return () => window.removeEventListener('appinstalled', handler);
  }, []);

  // Decide whether / what to show after the splash clears
  useEffect(() => {
    const timer = setTimeout(() => {
      const st = load();

      // Never show if already installed or running as standalone PWA
      if (st.installed) return;
      if (window.matchMedia('(display-mode: standalone)').matches) return;
      if (window.navigator.standalone === true) return;

      // Never show if we already showed the manual guide
      if (st.manualShown) return;

      const count = st.dismissCount || 0;

      if (count === 0) {
        // First visit
        show('banner');
      } else if (count === 1) {
        // Dismissed once — show again after 3 days
        const elapsed = Date.now() - (st.dismissedAt || 0);
        if (elapsed >= THREE_DAYS) show('banner');
      } else {
        // Dismissed twice — show manual guide once
        save({ manualShown: true });
        show('manual');
      }
    }, SHOW_DELAY_MS);

    return () => clearTimeout(timer);
  }, []); // run once on mount

  function show(p) {
    setPhase(p);
    // rAF so the slide-in transition actually runs
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  }

  function hide(cb) {
    setVisible(false);
    setTimeout(() => { setPhase(null); cb?.(); }, 420);
  }

  const handleDismiss = useCallback(() => {
    const st = load();
    const count = (st.dismissCount || 0) + 1;
    save({ dismissCount: count, dismissedAt: Date.now() });

    if (count >= 2) {
      // Second dismiss → show manual guide immediately
      hide(() => {
        save({ manualShown: true });
        show('manual');
      });
    } else {
      hide();
    }
  }, []);

  const handleInstall = useCallback(async () => {
    if (_deferredPrompt && !isIOS()) {
      try {
        const result = await _deferredPrompt.prompt();
        const outcome = result?.outcome ?? result;
        if (outcome === 'accepted') {
          save({ installed: true });
          hide();
          return;
        }
      } catch {
        // prompt() threw (e.g. already used) — fall through to manual
      }
      // User dismissed the native prompt — treat as dismiss
      _deferredPrompt = null;
      handleDismiss();
    } else {
      // iOS or no native prompt → show manual guide
      hide(() => {
        save({ manualShown: true });
        show('manual');
      });
    }
  }, [handleDismiss]);

  if (!phase) return null;

  if (phase === 'manual') {
    return (
      <ManualGuide
        visible={visible}
        tab={manualTab}
        onTabChange={setManualTab}
        onClose={() => hide()}
        t={t}
      />
    );
  }

  return (
    <InstallBanner
      visible={visible}
      ios={isIOS()}
      onInstall={handleInstall}
      onDismiss={handleDismiss}
      t={t}
    />
  );
}
